import { AnotacionesVigentesDto, BoletaRecibidaDto, BoletaTerceroEmitidaDto, ComunicacionSiiDto, DetalleF50Dto, DocumentoRegistroComprasVentasDto, MultaDto } from '../reportes-api.services';
import { ChartData } from 'chart.js';
import { ColumnaReporte, DatosReporte, Tabla } from '../core/reporte';
import { nombreMes } from './reportes-parser';

export type { GraficoTabla, SubTabla, Tabla } from '../core/reporte';
export type Columnas = ColumnaReporte;

export interface ReporteF50MesParseado {
    numeroMes: number;
    mes: string;
    estado: string;
    detalle: DetalleF50Dto[];
    noData: string;
}

export interface ReporteF50yF29Parseado {
    rut: string;
    generadoEn: string | null;
    meses: ReporteF50MesParseado[];
}

export interface ReporteDinamicoParseado {
    rut: string;
    titulo: string;
    generadoEn: string;
    tablas: Tabla[];
    data: Record<string, any[]>;
}

export interface DocumentoRegistroComprasVentasParseado extends DocumentoRegistroComprasVentasDto {
    mes: number;
    periodo: string;
}

export interface ReporteRegistroComprasVentasParseado {
    rut: string;
    anio: number;
    generadoEn: string | null;

    compras: DocumentoRegistroComprasVentasParseado[];
    ventas: DocumentoRegistroComprasVentasParseado[];
}

export interface ReporteComunicacionesParseado {
    rut: string;
    generadoEn: string | null;

    notificaciones: ComunicacionSiiDto[];
    cartasCorreosInformativos: ComunicacionSiiDto[];
    anotacionesVigentes: AnotacionesVigentesDto[]
}

export interface MesBoletasHonorariosParseado {
    id: number;
    mes: string;
    anio: number;
    cantidadBoletas: number;
    vigentes: number;
    anuladas: number;
    bruto: number;
    retenido: number;
    pagado: number;
    ultimaActualizacion: string;
    expandible: (BoletaRecibidaDto | BoletaTerceroEmitidaDto)[];
}

export interface ReporteBoletasHonorariosParseado {
    rut: string;
    contribuyente: string | null;
    anio: number;
    generadoEn: string;
    recibidas: MesBoletasHonorariosParseado[];
    emitidas: MesBoletasHonorariosParseado[];
    terceros: MesBoletasHonorariosParseado[];
}

export interface ReporteMultasParseado {
    rut: string;
    generadoEn: string | null;

    formulario21: MultaParseada[];
    formulario45: MultaParseada[];

    graficoFormulario21: ChartData<'line', number[], string>;
    graficoFormulario45: ChartData<'line', number[], string>;
}

export interface MultaParseada extends Omit<MultaDto, 'totalPagar'> {
    totalPagar: number | null;
}

export interface TipoOpcionReporte {
    id: string;
    nombre: string;
    tablas?: Tabla[];
}

export interface FormatoReportes extends DatosReporte {
    anio: boolean;
    mes?: boolean;
    tieneTipos?: boolean;
    tipos?: TipoOpcionReporte[];
}

const REPORTE_DATOS_TRIBUTARIOS: FormatoReportes = {
    tituloReporte: 'Datos Tributarios',
    icono: 'pi pi-table',

    subtituloReporte: 'Consulta de datos personales y tributarios del contribuyente (SII).',

    tablas: [],

    anio: false
};

const REPORTE_F22: FormatoReportes = {
    tituloReporte: 'Reporte F22',
    icono: 'pi pi-chart-bar',
    subtituloReporte: 'Consulta anual del Formulario 22 del SII.',
    tablas: [
        {
            datos: 'situacionRentaActual',
            titulo: "Situación Renta Actual",
            subtitulo: '',
            columnas: [
                {nombre: 'detalle', titulo: ""}
            ],
        },
        {
            datos: 'historial',
            titulo: 'Ultimo Ingreso en Historial',
            subtitulo: '',
            columnas: [
                { nombre: 'folio', titulo: 'Folio' },
                { nombre: 'fecha', titulo: 'Fecha', clase: 'text-center' },
                { nombre: 'descripcion', titulo: 'Descripción' }
            ]
        },
        {
            datos: 'observaciones',
            titulo: 'Observaciones',
            subtitulo: '',
            columnas: [
                { nombre: 'codigo', titulo: 'Codigó', clase: 'text-center w-[10%]' },
                { nombre: 'observacion', titulo: 'Observación', clase: 'w-[90%]' }
            ]
        },

        {
            datos: 'detalle',
            titulo: 'Detalle Formulario F22',
            subtitulo: 'Mostrando el detalle del Formulario 22 para el año',
            columnas: [
                { nombre: 'descripcion', titulo: 'Descripción' },
                { nombre: 'codigo', titulo: 'Codigó', clase: 'text-center' },
                { nombre: 'valor', titulo: 'Monto' }
            ]
        }
    ],
    anio: true
};

const REPORTE_F50: FormatoReportes = {
    tituloReporte: 'Reporte F50',
    icono: 'pi pi-chart-bar',
    subtituloReporte: 'Consulta anual del Formulario 50 con desglose mensual (SII).',
    tablas: [
        {
            datos: 'meses',
            titulo: '',
            subtitulo: 'Mostrando los 12 períodos mensuales para el año ',
            columnas: [
                { nombre: 'mes', titulo: 'Mes' },
                { nombre: 'estado', titulo: 'Estado', clase: 'whitespace-pre-line !text-center align-middle' },
                {
                    nombre: 'detalle',
                    titulo: 'Formulario 50',
                    subTabla: {
                        datos: 'detalle',
                        noData: 'No existe un reporte para este mes.',
                        columnas: [
                            { nombre: 'descripcion', titulo: 'Descripción' },
                            { nombre: 'codigo', titulo: 'Codigó' },
                            { nombre: 'monto', titulo: 'Monto' }
                        ]
                    }
                }
            ]
        }
    ],
    anio: true
};

const REPORTE_F29: FormatoReportes = {
    tituloReporte: 'Reporte F29',
    icono: 'pi pi-chart-bar',
    subtituloReporte: 'Consulta anual del Formulario 50 con desglose mensual (SII).',
    tablas: [
        {
            datos: 'meses',
            titulo: '',
            subtitulo: 'Mostrando los 12 períodos mensuales para el año ',
            columnas: [
                { nombre: 'mes', titulo: 'Mes' },
                { nombre: 'estado', titulo: 'Estado', clase: 'whitespace-pre-line !text-center align-middle' },
                {
                    nombre: 'detalle',
                    titulo: 'Formulario 50',
                    subTabla: {
                        datos: 'detalle',
                        noData: 'No existe un reporte para este mes.',
                        columnas: [
                            { nombre: 'descripcion', titulo: 'Descripción' },
                            { nombre: 'codigo', titulo: 'Codigó' },
                            { nombre: 'monto', titulo: 'Monto' }
                        ]
                    }
                }
            ]
        }
    ],
    anio: true
};

const REPORTE_DECLARACIONES_JURADAS: FormatoReportes = {
    tituloReporte: 'Declaraciones Juradas',
    icono: 'pi pi-table',

    subtituloReporte: 'Consulta anual de Declaraciones Juradas del SII.',

    tablas: [
        {
            datos: 'declaradas',
            titulo: 'Declaraciones Presentadas',
            subtitulo: 'Declaraciones juradas registradas para el año seleccionado.',
            columnas: [
                {
                    nombre: 'codigo',
                    titulo: 'Código'
                },
                {
                    nombre: 'descripcion',
                    titulo: 'Descripción'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Vencimiento'
                },
                {
                    nombre: 'estado',
                    titulo: 'Estado'
                },
                {
                    nombre: 'fechaPresentacion',
                    titulo: 'Presentación'
                }
            ]
        },
        {
            datos: 'otras',
            titulo: 'Otras Declaraciones Juradas',
            subtitulo: 'Otras declaraciones asociadas al contribuyente.',
            columnas: [
                {
                    nombre: 'codigo',
                    titulo: 'Código'
                },
                {
                    nombre: 'descripcion',
                    titulo: 'Descripción'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Vencimiento'
                }
            ]
        }
    ],

    anio: true
};

const MULTAS: FormatoReportes = {
    tituloReporte: 'Giros y Multas',
    icono: 'pi pi-chart-bar',

    subtituloReporte: 'Consulta de multas de los formularios 21 y 45 del contribuyente (SII).',

    tablas: [
        {
            datos: 'formulario21',

            titulo: 'Multas Formulario 21',

            subtitulo: 'Multas registradas para el Formulario 21.',

            grafico: {
                datos: 'graficoFormulario21',
                titulo: 'Total acumulado a pagar',
                subtitulo: 'Evolución mensual de la deuda del Formulario 21.',
                tipo: 'line'
            },

            columnas: [
                {
                    nombre: 'folio',
                    titulo: 'Folio'
                },
                {
                    nombre: 'fechaEmision',
                    titulo: 'Fecha emisión'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha vencimiento'
                },
                {
                    nombre: 'negocioEmisor',
                    titulo: 'Negocio emisor'
                },
                {
                    nombre: 'moneda',
                    titulo: 'Moneda'
                },
                {
                    nombre: 'formulario',
                    titulo: 'Formulario'
                },
                {
                    nombre: 'totalPagar',
                    titulo: 'Total a pagar',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                }
            ]
        },

        {
            datos: 'formulario45',

            titulo: 'Multas Formulario 45',

            subtitulo: 'Multas registradas para el Formulario 45.',

            grafico: {
                datos: 'graficoFormulario45',
                titulo: 'Total acumulado a pagar',
                subtitulo: 'Evolución mensual de la deuda del Formulario 45.',
                tipo: 'line'
            },

            columnas: [
                {
                    nombre: 'folio',
                    titulo: 'Folio'
                },
                {
                    nombre: 'fechaEmision',
                    titulo: 'Fecha emisión'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha vencimiento'
                },
                {
                    nombre: 'negocioEmisor',
                    titulo: 'Negocio emisor'
                },
                {
                    nombre: 'moneda',
                    titulo: 'Moneda'
                },
                {
                    nombre: 'formulario',
                    titulo: 'Formulario'
                },
                {
                    nombre: 'totalPagar',
                    titulo: 'Total a pagar',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                }
            ]
        }
    ],

    anio: false
};

const REPORTE_PROPIEDADES: FormatoReportes = {
    tituloReporte: 'Propiedades',
    icono: 'pi pi-building',

    subtituloReporte: 'Consulta de propiedades registradas para el contribuyente (SII).',

    tablas: [
        {
            datos: 'propiedades',

            titulo: 'Propiedades registradas',

            subtitulo: 'Bienes raíces asociados al contribuyente.',

            columnas: [
                {
                    nombre: 'comuna',
                    titulo: 'Comuna'
                },
                {
                    nombre: 'rol',
                    titulo: 'Rol'
                },
                {
                    nombre: 'direccion',
                    titulo: 'Dirección'
                },
                {
                    nombre: 'destino',
                    titulo: 'Destino'
                }
            ]
        }
    ],

    anio: false
};

const REPORTE_DEUDAS_PROPIEDADES: FormatoReportes = {
    tituloReporte: 'Deudas de propiedades',
    icono: 'pi pi-building',

    subtituloReporte: 'Consulta de cuotas de contribuciones vigentes y vencidas del contribuyente (SII).',

    tablas: [
        {
            datos: 'sobretasas',

            titulo: 'Deudas Sobretasa',

            subtitulo: '',

            columnas: [
                {
                    nombre: 'folio',
                    titulo: 'Folio'
                },
                {
                    nombre: 'fechaEmision',
                    titulo: 'Fecha Emision'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha Vencimiento'
                },
                {
                    nombre: 'negocioEmisor',
                    titulo: 'Negocio Emisor'
                },
                {
                    nombre: 'moneda',
                    titulo: 'Moneda'
                },
                {
                    nombre: 'form',
                    titulo: 'Formulario'
                },
                {
                    nombre: 'totalPagar',
                    titulo: 'Total a pagar'
                }
            ]
        },
        {
            datos: 'cuotasVigentes',

            titulo: 'Deudas Contribuciones Vigentes',

            subtitulo: 'Cuotas cuyo plazo de pago aún se encuentra vigente.',

            columnas: [
                {
                    nombre: 'comuna',
                    titulo: 'Comuna'
                },
                {
                    nombre: 'rol',
                    titulo: 'Rol'
                },
                {
                    nombre: 'tipoCuota',
                    titulo: 'Tipo de cuota'
                },
                {
                    nombre: 'numeroCuotaAnio',
                    titulo: 'N.º cuota / año'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha vencimiento'
                },
                {
                    nombre: 'estadoPago',
                    titulo: 'Estado de pago'
                },
                {
                    nombre: 'totalPagar',
                    titulo: 'Total a pagar'
                }
            ]
        },

        {
            datos: 'cuotasVencidas',

            titulo: 'Deudas Contribuciones Vencidas',

            subtitulo: '',

            columnas: [
                {
                    nombre: 'comuna',
                    titulo: 'Comuna'
                },
                {
                    nombre: 'rol',
                    titulo: 'Rol'
                },
                {
                    nombre: 'nroCuota',
                    titulo: 'N.º cuota'
                },
                {
                    nombre: 'anio',
                    titulo: 'Año'
                },
                {
                    nombre: 'valorCuotaDentroPlazo',
                    titulo: 'Valor Cuota en Plazo'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha vencimiento'
                },
                {
                    nombre: 'totalPagar',
                    titulo: 'Total a pagar'
                }
            ]
        }
    ],

    anio: false
};

const REPORTE_PETICIONES_ADMINISTRATIVAS: FormatoReportes = {
    tituloReporte: 'Peticiones administrativas',
    icono: 'pi pi-inbox',

    subtituloReporte: 'Consulta de peticiones administrativas registradas para el contribuyente (SII).',

    tablas: [
        {
            datos: 'peticiones',

            titulo: 'Peticiones administrativas',

            subtitulo: 'Solicitudes administrativas asociadas al contribuyente.',

            columnas: [
                {
                    nombre: 'folio',
                    titulo: 'Folio'
                },
                {
                    nombre: 'rut',
                    titulo: 'RUT'
                },
                {
                    nombre: 'nombre',
                    titulo: 'Nombre'
                },
                {
                    nombre: 'materia',
                    titulo: 'Materia'
                },
                {
                    nombre: 'recepcion',
                    titulo: 'Recepción'
                },
                {
                    nombre: 'estado',
                    titulo: 'Estado'
                },
                {
                    nombre: 'descripcion',
                    titulo: 'Descripción'
                }
            ]
        }
    ],

    anio: false
};

const REPORTE_IVA_POSTERGADO: FormatoReportes = {
    tituloReporte: 'IVA postergado',
    icono: 'pi pi-calendar-clock',

    subtituloReporte: 'Consulta anual de giros asociados al pago postergado de IVA del contribuyente (SII).',

    tablas: [
        {
            datos: 'registros',

            titulo: 'Registros de IVA postergado',

            subtitulo: 'Giros correspondientes al año seleccionado.',

            columnas: [
                {
                    nombre: 'periodo',
                    titulo: 'Período'
                },
                {
                    nombre: 'folioGiro',
                    titulo: 'Folio giro'
                },
                {
                    nombre: 'formularioGiro',
                    titulo: 'Formulario giro'
                },
                {
                    nombre: 'formularioAsociado',
                    titulo: 'Formulario asociado'
                },
                {
                    nombre: 'fechaEmision',
                    titulo: 'Fecha emisión'
                },
                {
                    nombre: 'fechaVencimiento',
                    titulo: 'Fecha vencimiento'
                },
                {
                    nombre: 'estado',
                    titulo: 'Estado'
                },
                {
                    nombre: 'moneda',
                    titulo: 'Moneda'
                },
                {
                    nombre: 'saldo',
                    titulo: 'Saldo'
                }
            ]
        }
    ],

    anio: true
};

const F29_OBSERVADO: FormatoReportes = {
    tituloReporte: 'F29 Observado',
    icono: 'pi pi-exclamation-circle',
    subtituloReporte: 'Consulta de períodos observados del Formulario 29 del contribuyente (SII).',
    tablas: [],
    anio: false,
    plantilla: 'f29-observado'
};
const AGENTES_RETENEDORES: FormatoReportes = {
    tituloReporte: 'Agentes retenedores',
    icono: 'pi pi-file-check',
    subtituloReporte: 'Consulta anual de formularios informados por agentes retenedores del contribuyente (SII).',
    tablas: [],
    anio: true,
    plantilla: 'agentes-retenedores'
};

const REPORTE_REGISTRO_COMPRAS_VENTAS: FormatoReportes = {
    tituloReporte: 'Registro de Compras y Ventas',
    icono: 'pi pi-book',

    subtituloReporte: 'Consulta anual del Registro de Compras y Ventas del contribuyente (SII).',

    anio: true,
    mes: false,

    tablas: [
        {
            datos: 'compras',

            titulo: 'Registro de Compras',

            subtitulo: 'Documentos de compra registrados durante el año seleccionado.',

            columnas: [
                {
                    nombre: 'periodo',
                    titulo: 'Período'
                },
                {
                    nombre: 'nombreTipoDocumento',
                    titulo: 'Tipo Documento'
                },
                {
                    nombre: 'totalDocumentos',
                    titulo: 'Total Documentos'
                },
                {
                    nombre: 'montoExento',
                    titulo: 'Monto Exento',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoNeto',
                    titulo: 'Monto Neto',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoIva',
                    titulo: 'Monto IVA',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoIvaUsoComun',
                    titulo: 'IVA Uso Común',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoIvaNoRecuperable',
                    titulo: 'IVA No Recuperable',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoTotal',
                    titulo: 'Monto Total',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                }
            ]
        },
        {
            datos: 'ventas',

            titulo: 'Registro de Ventas',

            subtitulo: 'Documentos de venta registrados durante el año seleccionado.',

            columnas: [
                {
                    nombre: 'periodo',
                    titulo: 'Período'
                },
                {
                    nombre: 'nombreTipoDocumento',
                    titulo: 'Tipo Documento'
                },
                {
                    nombre: 'totalDocumentos',
                    titulo: 'Total Documentos'
                },
                {
                    nombre: 'montoExento',
                    titulo: 'Monto Exento',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoNeto',
                    titulo: 'Monto Neto',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoIva',
                    titulo: 'Monto IVA',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                },
                {
                    nombre: 'montoTotal',
                    titulo: 'Monto Total',
                    formato: 'moneda',
                    clase: 'whitespace-nowrap text-right'
                }
            ]
        }
    ]
};

const COLUMNAS_DETALLE_BOLETAS: Columnas[] = [
    { nombre: 'numero', titulo: 'N°', clase: 'font-mono' },
    { nombre: 'estado', titulo: 'Estado', formato: 'estado' },
    { nombre: 'fechaEmision', titulo: 'Emisión', formato: 'fecha' },
    { nombre: 'nombreEmisor', titulo: 'Emisor', formato: 'persona', secundario: 'rutEmisor' },
    { nombre: 'nombreReceptor', titulo: 'Receptor', formato: 'persona', secundario: 'rutReceptor' },
    { nombre: 'fechaRecepcion', titulo: 'Recepción', formato: 'fecha' },
    { nombre: 'honorariosBrutos', titulo: 'Bruto', clase: 'whitespace-nowrap text-right', formato: 'moneda' },
    { nombre: 'honorariosRetenidos', titulo: 'Retenido', clase: 'whitespace-nowrap text-right', formato: 'moneda' },
    { nombre: 'honorariosPagados', titulo: 'Pagado', clase: 'whitespace-nowrap text-right', formato: 'moneda' }
];

const COLUMNAS_DETALLE_BOLETAS_RECIBIDAS: Columnas[] = [
    { nombre: 'numero', titulo: 'N°', clase: 'font-mono' },
    { nombre: 'estado', titulo: 'Estado', formato: 'estado' },
    { nombre: 'fechaEmision', titulo: 'Emisión', formato: 'fecha' },
    { nombre: 'nombreEmisor', titulo: 'Emisor', formato: 'persona', secundario: 'rutEmisor' },
    { nombre: 'sociedadProfesional', titulo: 'Sociedad profesional', clase: 'text-center' },
    { nombre: 'honorariosBrutos', titulo: 'Bruto', clase: 'whitespace-nowrap text-right', formato: 'moneda' },
    { nombre: 'honorariosRetenidos', titulo: 'Retenido', clase: 'whitespace-nowrap text-right', formato: 'moneda' },
    { nombre: 'honorariosPagados', titulo: 'Pagado', clase: 'whitespace-nowrap text-right', formato: 'moneda' }
];

const TIPOS_TABLAS_BOLETAS = [
    { datos: 'recibidas', titulo: 'Boletas recibidas', subtitulo: 'Resumen mensual de boletas de honorarios recibidas.', columnasDetalle: COLUMNAS_DETALLE_BOLETAS_RECIBIDAS },
    { datos: 'emitidas', titulo: 'Boletas emitidas', subtitulo: 'Resumen mensual de boletas de honorarios emitidas.', columnasDetalle: COLUMNAS_DETALLE_BOLETAS },
    { datos: 'terceros', titulo: 'Boletas de terceros', subtitulo: 'Resumen mensual de boletas emitidas por terceros.', columnasDetalle: COLUMNAS_DETALLE_BOLETAS }
] as const;

const REPORTE_BOLETAS_HONORARIOS: FormatoReportes = {
    tituloReporte: 'Boletas de Honorarios',
    icono: 'pi pi-receipt',
    subtituloReporte: 'Matriz anual consolidada de boletas electrónicas con desglose mensual (SII).',
    anio: true,
    plantilla: 'tabla-expandible',
    tablas: TIPOS_TABLAS_BOLETAS.map((tipo) => {
        const { columnasDetalle, ...tabla } = tipo;

        return {
            ...tabla,
            columnas: [
                {
                    nombre: 'expandible',
                    titulo: 'Expandible',
                    clase: 'w-28 text-center',
                    subTabla: {
                        datos: 'expandible',
                        noData: `No hay detalle disponible para las boletas ${tipo.datos}.`,
                        columnas: columnasDetalle
                    }
                },
                { nombre: 'mes', titulo: 'Mes', clase: 'min-w-36 font-medium', formato: 'mesAnio' },
                { nombre: 'cantidadBoletas', titulo: 'N° Boletas', clase: 'w-32 text-center font-medium' },
                { nombre: 'bruto', titulo: 'Honorario Bruto', clase: 'min-w-36 whitespace-nowrap text-right font-medium', formato: 'moneda' },
                { nombre: 'retenido', titulo: 'Retención', clase: 'min-w-32 whitespace-nowrap text-right font-medium', formato: 'moneda' },
                { nombre: 'pagado', titulo: 'Total Líquido', clase: 'min-w-36 whitespace-nowrap text-right font-medium', formato: 'moneda' },
                { nombre: 'ultimaActualizacion', titulo: 'Última Actualización', clase: 'min-w-44 whitespace-nowrap text-xs text-surface-500', formato: 'fechaHora' }
            ]
        };
    })
};

const REPORTE_COMUNICACIONES: FormatoReportes = {
    tituloReporte: 'Comunicaciones del SII',
    icono: 'pi pi-bell',

    subtituloReporte: 'Notificaciones, cartas y correos informativos publicados por el Servicio de Impuestos Internos.',

    anio: false,
    mes: false,

    tablas: [
        {
            datos: 'anotacionesVigentes',
            titulo: 'Anotaciones Vigentes',
            subtitulo: '',
            columnas: [
                {nombre: 'anotacion', titulo: "Anotación"},
                {nombre: 'cantidad', titulo: "Cantidad"},
                {nombre: 'fechaActivacion', titulo: "Fecha de Activacion"},
                {nombre: 'descripcion', titulo: "Descripción y Efectos"},
                {nombre: 'accion', titulo: "Acción Requerida"}
            ]
        },
        {
            datos: 'notificaciones',

            titulo: 'Notificaciones',

            subtitulo: 'Notificaciones publicadas por el Servicio de Impuestos Internos.',

            columnas: [
                {
                    nombre: 'id',
                    titulo: 'ID'
                },
                {
                    nombre: 'fecha',
                    titulo: 'Fecha'
                },
                {
                    nombre: 'descripcion',
                    titulo: 'Descripción'
                },
                {
                    nombre: 'periodo',
                    titulo: 'Período'
                },
                {
                    nombre: 'descripcionTipo',
                    titulo: 'Tipo'
                },
                {
                    nombre: 'origen',
                    titulo: 'Origen'
                },
                {
                    nombre: 'tipoNotificacion',
                    titulo: 'Tipo de notificación'
                }
            ]
        },

        {
            datos: 'cartasCorreosInformativos',

            titulo: 'Cartas y correos informativos',

            subtitulo: 'Cartas y correos informativos publicados por el Servicio de Impuestos Internos.',

            columnas: [
                {
                    nombre: 'id',
                    titulo: 'ID'
                },
                {
                    nombre: 'fecha',
                    titulo: 'Fecha'
                },
                {
                    nombre: 'descripcion',
                    titulo: 'Descripción'
                },
                {
                    nombre: 'periodo',
                    titulo: 'Período'
                },
                {
                    nombre: 'descripcionTipo',
                    titulo: 'Tipo'
                },
                {
                    nombre: 'origen',
                    titulo: 'Origen'
                },
                {
                    nombre: 'tipoNotificacion',
                    titulo: 'Tipo de notificación'
                }
            ]
        }
    ]
};

const EXPEDIENTES_TRIBUTARIOS: FormatoReportes = {
    tituloReporte: 'Expedientes Tributarios',
    icono: 'pi pi-exclamation-circle',

    subtituloReporte: 'Consulta de expedientes tributarios asociados al contribuyente (SII).',

    tablas: [
        {
            datos: 'expedientes',

            titulo: 'Expedientes Tributarios',

            subtitulo: 'Expedientes registrados para el contribuyente.',

            columnas: [
                {
                    nombre: 'idExpediente',
                    titulo: 'Id'
                },
                {
                    nombre: 'tipoNegocio',
                    titulo: 'Tipo de operación'
                },
                {
                    nombre: 'estado',
                    titulo: 'Estado'
                },
                {
                    nombre: 'fechaCreacion',
                    titulo: 'Fecha de creación'
                }
            ]
        }
    ],

    anio: false
};


const DEVOLUCIONES_IVA: FormatoReportes = {
    tituloReporte: 'Devoluciones IVA',
    icono: 'pi pi-file',
    subtituloReporte: 'Consulta de solicitudes de devolución del Formulario 3280 del SII.',
    tablas: [
        {
            datos: 'periodos',
            titulo: 'Solicitudes F3280',
            subtitulo: 'Mostrando los períodos con información para el año ',
            columnas: [
                {
                    nombre: 'periodo',
                    titulo: 'Periodo',
                    clase: 'text-center'
                },
                {
                    nombre: 'folio',
                    titulo: 'Folio',
                    clase: 'text-center'
                },
                {
                    nombre: 'evigCod',
                    titulo: 'EvigCod',
                    clase: 'text-center'
                },
                {
                    nombre: 'pago',
                    titulo: 'Pago',
                    clase: 'text-right'
                },
                {
                    nombre: 'estado',
                    titulo: 'Estado',
                    clase: 'text-center'
                },
                {
                    nombre: 'historial',
                    titulo: 'Historial de eventos',
                    subTabla: {
                        datos: 'historial',
                        noData: 'No existen eventos registrados para este período.',
                        columnas: [
                            {
                                nombre: 'fecha',
                                titulo: 'Fecha',
                                clase: 'text-center'
                            },
                            {
                                nombre: 'descripcion',
                                titulo: 'Descripción'
                            }
                        ]
                    }
                }
            ]
        }
    ],
    anio: true,
    plantilla: 'devoluciones-iva'

};

export const SII_REPORTES_DATOS = {
    datosTributarios: REPORTE_DATOS_TRIBUTARIOS,
    f22: REPORTE_F22,
    f29: REPORTE_F29,
    f50: REPORTE_F50,
    declaracionesJuradas: REPORTE_DECLARACIONES_JURADAS,
    multas: MULTAS,
    propiedades: REPORTE_PROPIEDADES,
    deudasPropiedades: REPORTE_DEUDAS_PROPIEDADES,
    peticionesAdministrativas: REPORTE_PETICIONES_ADMINISTRATIVAS,
    ivaPostergado: REPORTE_IVA_POSTERGADO,
    f29Observado: F29_OBSERVADO,
    expedientesTributarios: EXPEDIENTES_TRIBUTARIOS,
    agentesRetenedores: AGENTES_RETENEDORES,
    boletas: REPORTE_BOLETAS_HONORARIOS,
    registroComprasVentas: REPORTE_REGISTRO_COMPRAS_VENTAS,
    comunicaciones: REPORTE_COMUNICACIONES,
    devolucionesIva: DEVOLUCIONES_IVA
} satisfies Record<string, FormatoReportes>;

export type TipoReporteSii = keyof typeof SII_REPORTES_DATOS;
